import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { startOfWeek, endOfWeek, addWeeks, addMonths, addYears, startOfMonth, endOfMonth, startOfYear, endOfYear, eachDayOfInterval, format, isSameMonth, isToday } from "date-fns";
import { fr } from "date-fns/locale";
import { api } from "@/lib/api";
import { fmtEUR, STATUS_STYLES } from "@/constants";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Ban, Trash2, Truck, PackageCheck } from "lucide-react";

function EventChip({ r, boothNames, compact }) {
  const navigate = useNavigate();
  const style = STATUS_STYLES[r.status] || STATUS_STYLES["Demande"];
  return (
    <button
      data-testid={`agenda-card-event-${r.id}`}
      onClick={() => navigate(`/reservations/${r.id}`)}
      className={`w-full text-left rounded-lg border-l-4 bg-white border border-slate-200 px-2 py-1.5 hover:shadow-sm transition-shadow ${compact ? "text-[10px]" : "text-xs"}`}
      style={{ borderLeftColor: "currentColor" }}
    >
      <div className="flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full shrink-0 ${style.dot}`} />
        <span className="font-semibold truncate">{r.event?.city}</span>
        {!compact && <span className="ml-auto font-mono tabular-nums text-slate-500">{fmtEUR(r.pricing?.total)}</span>}
      </div>
      {!compact && (
        <p className="text-slate-500 truncate mt-0.5 pl-3.5">
          {r.client?.name} · {r.event?.type}
          {r.logistics?.booth_id !== null && r.logistics?.booth_id !== undefined && r.logistics?.booth_id !== "" ? ` · ${boothNames[r.logistics.booth_id]}` : ""}
        </p>
      )}
    </button>
  );
}

function CompactLine({ r, boothNames }) {
  const navigate = useNavigate();
  const kind = r.kind || "photobooth";
  const booth = r.logistics?.booth_id;
  const hasBooth = booth !== null && booth !== undefined && booth !== "";
  const product = kind === "agb" ? "Audio Guest Book"
    : kind === "cabine" ? "Cabine Audio Guest Book"
    : `${hasBooth ? (boothNames[booth] || `PB${booth + 1}`) : "PB à attribuer"} — ${r.pricing?.formula || "?"} photos`;
  const type = r.event?.type === "Autre" && r.event?.type_other ? r.event.type_other : r.event?.type;
  return (
    <button
      data-testid={`agenda-day-summary-${r.id}`}
      onClick={() => navigate(`/reservations/${r.id}`)}
      className="w-full text-left rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm hover:bg-sky-50 hover:border-sky-300 transition-colors truncate"
    >
      <span className="font-semibold">{r.client?.name}</span>
      <span className="text-slate-500"> — {type} — {product}</span>
    </button>
  );
}

function MobileDayCard({ d, events, deliveries, pickups, block, boothNames }) {
  const navigate = useNavigate();
  const key = format(d, "yyyy-MM-dd");
  const we = [6, 0].includes(d.getDay());
  const unassigned = events.filter((r) => r.logistics?.booth_id === null || r.logistics?.booth_id === undefined);
  return (
    <div data-testid={`agenda-day-${key}`} className={`rounded-2xl border p-4 ${block ? (block.provisional ? "border-amber-300 bg-amber-50/70" : "border-rose-300 bg-rose-50/70") : we ? "border-sky-400 bg-sky-50/70" : "border-slate-200 bg-white"} ${isToday(d) ? "ring-2 ring-sky-500" : ""}`}>
      <p className={`font-heading font-extrabold uppercase tracking-tight ${we ? "text-sky-800 text-lg" : "text-slate-700"}`}>
        {format(d, "EEEE d MMMM", { locale: fr })}
      </p>
      {block && <p data-testid={`agenda-block-${key}`} className={`text-xs font-semibold rounded px-1.5 py-0.5 mt-1.5 inline-block ${block.provisional ? "text-amber-800 bg-amber-100" : "text-rose-700 bg-rose-100"}`}>{block.provisional ? "🟠 Période à confirmer" : "Indisponible"} — {block.reason}{block.relais_possible ? " (relais Stéphane)" : ""}</p>}
      <div className="mt-2 space-y-1.5">
        {[0, 1, 2].map((i) => {
          const ev = events.find((r) => r.logistics?.booth_id === i);
          if (!ev) return <p key={i} className="text-xs text-emerald-600 font-semibold px-1">{boothNames[i] || `Photobooth ${i + 1}`} · Disponible</p>;
          return (
            <button key={i} data-testid={`agenda-card-event-${ev.id}`} onClick={() => navigate(`/reservations/${ev.id}`)} className="w-full text-left rounded-lg bg-white border border-slate-200 px-3 py-2 hover:shadow-sm transition-shadow">
              <p className="text-sm font-semibold">{boothNames[i] || `Photobooth ${i + 1}`} — {ev.event?.type} · {ev.event?.city}</p>
              <p className="text-xs text-slate-500">{ev.client?.name} · {fmtEUR(ev.pricing?.total)}{ev.logistics?.delivery_at ? ` · livraison ${format(new Date(ev.logistics.delivery_at), "EEE HH'h'mm", { locale: fr })}` : ""}</p>
            </button>
          );
        })}
        {unassigned.map((r) => (
          <button key={r.id} data-testid={`agenda-card-event-${r.id}`} onClick={() => navigate(`/reservations/${r.id}`)} className="w-full text-left rounded-lg bg-white border border-slate-200 px-3 py-2 hover:shadow-sm transition-shadow">
            <p className="text-sm font-semibold">{r.kind === "cabine" ? "Cabine Audio Guest Book" : "Photobooth à attribuer"} — {r.event?.type} · {r.event?.city}</p>
            <p className="text-xs text-slate-500">{r.client?.name} · {fmtEUR(r.pricing?.total)}</p>
          </button>
        ))}
      </div>
      {(deliveries.length > 0 || pickups.length > 0) && (
        <div className="mt-2 pt-2 border-t border-slate-200/60 space-y-1">
          {deliveries.map((r) => <p key={`d${r.id}`} className="text-xs text-slate-600 flex items-center gap-1.5"><Truck className="h-3 w-3 text-sky-600" /> {(r.kind || "photobooth") === "photobooth" ? "Livraison →" : "Retrait →"} {r.event?.city} ({r.client?.name})</p>)}
          {pickups.map((r) => <p key={`p${r.id}`} className="text-xs text-slate-600 flex items-center gap-1.5"><PackageCheck className="h-3 w-3 text-slate-500" /> {(r.kind || "photobooth") === "photobooth" ? "Reprise ←" : "Retour ←"} {r.event?.city} ({r.client?.name})</p>)}
        </div>
      )}
      {events.length === 0 && deliveries.length === 0 && pickups.length === 0 && !block && <p className="text-xs text-slate-300 mt-1">—</p>}
    </div>
  );
}

export default function Agenda() {
  const [view, setViewState] = useState(() => localStorage.getItem("pb54_agenda_view") || "week");
  const [cursor, setCursor] = useState(new Date());
  const [prevView, setPrevView] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [items, setItems] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [boothNames, setBoothNames] = useState([]);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockForm, setBlockForm] = useState({ start_date: "", end_date: "", all_day: true, start_time: "", end_time: "", reason: "Congés", comment: "", relais_possible: false, block_type: "total", provisional: false, scope: [] });
  const BLOCK_REASONS = ["Congés", "Indisponibilité personnelle", "Maintenance", "Matériel indisponible", "Autre"];
  const BLOCK_TYPES = [
    ["total", "Indisponibilité totale"],
    ["relais", "Indisponibilité personnelle — relais possible"],
    ["provisoire", "Période provisoire — à confirmer"],
    ["maintenance", "Maintenance matériel"],
    ["autre", "Autre"],
  ];
  const applyBlockType = (t) => setBlockForm((f) => ({
    ...f, block_type: t,
    relais_possible: t === "relais",
    provisional: t === "provisoire",
    reason: t === "maintenance" ? "Maintenance" : f.reason,
  }));

  useEffect(() => { api.get("/settings").then((r) => setBoothNames(r.data.booth_names)); }, []);

  const setView = (v) => { setViewState(v); localStorage.setItem("pb54_agenda_view", v); };

  const days = useMemo(() => {
    if (view === "week") {
      return eachDayOfInterval({ start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) });
    }
    if (view === "month") {
      return eachDayOfInterval({
        start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }),
        end: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }),
      });
    }
    return eachDayOfInterval({ start: startOfYear(cursor), end: endOfYear(cursor) });
  }, [view, cursor]);

  useEffect(() => {
    const params = { date_from: format(days[0], "yyyy-MM-dd"), date_to: format(days[days.length - 1], "yyyy-MM-dd") };
    api.get("/reservations", { params }).then((r) => setItems(r.data));
    api.get("/unavailabilities", { params }).then((r) => setBlocks(r.data));
  }, [days]);

  const saveBlock = async () => {
    if (!blockForm.start_date || !blockForm.end_date) return;
    try {
      await api.post("/unavailabilities", blockForm);
      const params = { date_from: format(days[0], "yyyy-MM-dd"), date_to: format(days[days.length - 1], "yyyy-MM-dd") };
      const r = await api.get("/unavailabilities", { params });
      setBlocks(r.data);
      setShowBlockForm(false);
      setBlockForm({ start_date: "", end_date: "", all_day: true, start_time: "", end_time: "", reason: "Congés", comment: "", relais_possible: false, block_type: "total", provisional: false, scope: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const removeBlock = async (uid) => {
    await api.delete(`/unavailabilities/${uid}`);
    setBlocks(blocks.filter((b) => b.id !== uid));
  };

  const confirmBlock = async (uid) => {
    await api.post(`/unavailabilities/${uid}/confirm`);
    const params = { date_from: format(days[0], "yyyy-MM-dd"), date_to: format(days[days.length - 1], "yyyy-MM-dd") };
    const r = await api.get("/unavailabilities", { params });
    setBlocks(r.data);
    toast.success("Période confirmée en indisponibilité totale");
  };

  const blockFor = (key) => blocks.find((b) => b.start_date <= key && b.end_date >= key);

  const byDay = useMemo(() => {
    const map = {};
    for (const r of items) {
      const d = r.event?.date;
      if (!d) continue;
      (map[d] = map[d] || []).push(r);
    }
    return map;
  }, [items]);

  const byDelivery = useMemo(() => {
    const map = {};
    for (const r of items) {
      const d = (r.logistics?.delivery_at || "").slice(0, 10);
      if (d) (map[d] = map[d] || []).push(r);
    }
    return map;
  }, [items]);

  const byPickup = useMemo(() => {
    const map = {};
    for (const r of items) {
      const d = (r.logistics?.pickup_at || "").slice(0, 10);
      if (d) (map[d] = map[d] || []).push(r);
    }
    return map;
  }, [items]);

  const title = view === "week"
    ? `Semaine du ${format(days[0], "d MMMM", { locale: fr })}`
    : view === "month"
    ? format(cursor, "MMMM yyyy", { locale: fr })
    : format(cursor, "yyyy");

  const isWeekendKey = (d) => [5, 6, 0].includes(d.getDay());

  const DAY_PRIORITY = { 6: 0, 0: 1, 5: 2, 1: 3, 2: 4, 3: 5, 4: 6 };
  const mobileDays = useMemo(() => [...days].sort((a, b) => DAY_PRIORITY[a.getDay()] - DAY_PRIORITY[b.getDay()]), [days]);

  const yearStats = useMemo(() => {
    if (view !== "year") return [];
    return Array.from({ length: 12 }, (_, m) => {
      const key = `${cursor.getFullYear()}-${String(m + 1).padStart(2, "0")}`;
      const list = items.filter((r) => (r.event?.date || "").startsWith(key));
      const sats = new Set(list.map((r) => r.event.date).filter((d) => new Date(d + "T00:00:00").getDay() === 6));
      return { month: m, count: list.length, weekends: sats.size };
    });
  }, [view, items, cursor]);

  const shift = (dir) => setCursor(view === "week" ? addWeeks(cursor, dir) : view === "month" ? addMonths(cursor, dir) : addYears(cursor, dir));
  const touchX = useRef(null);
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (dx <= -60) shift(1);
    else if (dx >= 60) shift(-1);
  };
  const openMonth = (m) => { const d = new Date(cursor); d.setDate(1); d.setMonth(m); setCursor(d); setPrevView("year"); setView("month"); setSelectedDay(null); };
  const openWeek = (dateStr) => { setCursor(new Date(dateStr + "T00:00:00")); setPrevView("month"); setSelectedDay(null); setView("week"); };

  return (
    <div className="space-y-4" data-testid="agenda-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight capitalize">{title}</h1>
          {prevView && (
            <Button variant="ghost" size="sm" data-testid="agenda-back" onClick={() => { setView(prevView); setPrevView(null); setSelectedDay(null); }}>← Retour</Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 bg-white overflow-hidden">
            {[["week", "Semaine", "agenda-toggle-week"], ["month", "Mois", "agenda-toggle-month"], ["year", "Année", "agenda-toggle-year"]].map(([v, label, tid]) => (
              <button key={v} data-testid={tid} onClick={() => { setView(v); setPrevView(null); setSelectedDay(null); }}
                className={`px-4 py-2.5 text-sm font-semibold transition-colors ${view === v ? "bg-slate-900 text-white" : "text-slate-600"}`}>{label}</button>
            ))}
          </div>
          <Button variant="outline" size="sm" data-testid="agenda-add-unavailability" onClick={() => setShowBlockForm(!showBlockForm)}>
            <Ban className="h-4 w-4 mr-1" /> Indisponibilité
          </Button>
          <Button variant="outline" size="icon" data-testid="agenda-prev" onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" data-testid="agenda-today" onClick={() => { setCursor(new Date()); setSelectedDay(null); }}>Aujourd'hui</Button>
          <Button variant="outline" size="icon" data-testid="agenda-next" onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {showBlockForm && (
        <div data-testid="unavailability-form" className="rounded-2xl border border-rose-200 bg-rose-50/50 p-4 space-y-3">
          <h2 className="font-heading font-semibold">Bloquer une période</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1 sm:col-span-3">
              <label className="text-xs font-semibold text-slate-600">Type de blocage</label>
              <select data-testid="unavailability-type" value={blockForm.block_type} onChange={(e) => applyBlockType(e.target.value)} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                {BLOCK_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Date de début</label>
              <Input data-testid="unavailability-start" type="date" value={blockForm.start_date} onChange={(e) => setBlockForm({ ...blockForm, start_date: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Date de fin</label>
              <Input data-testid="unavailability-end" type="date" value={blockForm.end_date} onChange={(e) => setBlockForm({ ...blockForm, end_date: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600">Raison (interne)</label>
              <select data-testid="unavailability-reason" value={blockForm.reason} onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm">
                {BLOCK_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-1">
              <input data-testid="unavailability-all-day" type="checkbox" checked={blockForm.all_day} onChange={(e) => setBlockForm({ ...blockForm, all_day: e.target.checked })} className="h-4 w-4 accent-rose-600" />
              Journée entière
            </label>
            {!blockForm.provisional && (
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input data-testid="unavailability-relais" type="checkbox" checked={blockForm.relais_possible} onChange={(e) => setBlockForm({ ...blockForm, relais_possible: e.target.checked })} className="h-4 w-4 accent-rose-600" />
                Prestation potentiellement possible avec Stéphane
              </label>
            )}
            {blockForm.provisional && (
              <div className="sm:col-span-3 space-y-1.5 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="unavailability-scope">
                <p className="text-xs font-semibold text-amber-800">Périmètre du blocage — aucune case cochée = toute l'activité</p>
                <div className="flex flex-wrap gap-4">
                  {[["photobooth", "Photobooth"], ["agb", "Audio Guest Book"], ["cabine", "Cabine"]].map(([v, l]) => (
                    <label key={v} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" data-testid={`unavailability-scope-${v}`} checked={blockForm.scope.includes(v)}
                        onChange={(e) => setBlockForm({ ...blockForm, scope: e.target.checked ? [...blockForm.scope, v] : blockForm.scope.filter((x) => x !== v) })}
                        className="h-4 w-4 accent-amber-600" />
                      {l}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {!blockForm.all_day && (
              <>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">De</label>
                  <Input data-testid="unavailability-start-time" type="time" value={blockForm.start_time} onChange={(e) => setBlockForm({ ...blockForm, start_time: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">À</label>
                  <Input data-testid="unavailability-end-time" type="time" value={blockForm.end_time} onChange={(e) => setBlockForm({ ...blockForm, end_time: e.target.value })} />
                </div>
              </>
            )}
            <div className="sm:col-span-3 space-y-1">
              <label className="text-xs font-semibold text-slate-600">Commentaire / motif (interne)</label>
              <Input data-testid="unavailability-comment" value={blockForm.comment} onChange={(e) => setBlockForm({ ...blockForm, comment: e.target.value })} placeholder="Ex. vacances, révision machine…" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="unavailability-save" onClick={saveBlock} className={blockForm.provisional ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"}>
              {blockForm.provisional ? "Marquer la période à confirmer" : "Bloquer la période"}
            </Button>
            <Button variant="ghost" onClick={() => setShowBlockForm(false)}>Annuler</Button>
          </div>
          <p className="text-xs text-slate-500">La raison reste strictement interne : le site public affiche uniquement « Indisponible à cette date » ou « Disponibilité à confirmer » pour les périodes provisoires.</p>
        </div>
      )}

      {view === "week" && (
        <>
          {/* Mobile : samedi, dimanche, vendredi d'abord */}
          <div className="space-y-3 lg:hidden" data-testid="agenda-week-mobile">
            {mobileDays.map((d) => {
              const key = format(d, "yyyy-MM-dd");
              return <MobileDayCard key={key} d={d} events={byDay[key] || []} deliveries={byDelivery[key] || []} pickups={byPickup[key] || []} block={blockFor(key)} boothNames={boothNames} />;
            })}
          </div>
          {/* Desktop : grille classique lundi → dimanche */}
          <div className="hidden lg:grid lg:grid-cols-7 gap-3" data-testid="agenda-week-view">
            {days.map((d) => {
              const key = format(d, "yyyy-MM-dd");
              const dayEvents = byDay[key] || [];
              const highlight = isWeekendKey(d);
              const block = blockFor(key);
              return (
                <div
                  key={key}
                  data-testid={`agenda-day-desktop-${key}`}
                  className={`rounded-xl border p-3 min-h-[90px] ${block ? (block.provisional ? "border-amber-300 bg-amber-50/70" : "border-rose-300 bg-rose-50/70") : highlight ? "border-sky-300 bg-sky-50/60" : "border-slate-200 bg-white"} ${isToday(d) ? "ring-2 ring-sky-500" : ""}`}
                >
                  <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${block ? "text-rose-700" : highlight ? "text-sky-700" : "text-slate-500"}`}>
                    {format(d, "EEEE d", { locale: fr })}
                  </p>
                  {block && <p className={`text-[11px] font-semibold rounded px-1.5 py-0.5 mb-1.5 ${block.provisional ? "text-amber-800 bg-amber-100" : "text-rose-700 bg-rose-100"}`}>{block.provisional ? "🟠 À confirmer" : "Indisponible"} — {block.reason}</p>}
                  <div className="space-y-1.5">
                    {dayEvents.map((r) => <EventChip key={r.id} r={r} boothNames={boothNames} />)}
                    {dayEvents.length === 0 && <p className="text-[11px] text-slate-300">—</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {view === "month" && (
        <>
          <p className="text-[11px] text-slate-400 text-center lg:hidden">← Glisser pour changer de mois →</p>
          <div
            className="rounded-xl border border-slate-200 bg-white overflow-hidden"
            data-testid="agenda-month-view"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="grid grid-cols-7 border-b border-slate-200">
              {["lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim."].map((d, i) => (
                <div key={d} className={`px-2 py-2 text-xs font-bold uppercase tracking-wider text-center ${i >= 5 ? "text-sky-800 bg-sky-100" : i === 4 ? "text-sky-700 bg-sky-50" : "text-slate-500"}`}>{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {days.map((d) => {
                const key = format(d, "yyyy-MM-dd");
                const dayEvents = byDay[key] || [];
                const dayBlock = blockFor(key);
                const extraMoves = (byDelivery[key] || []).length + (byPickup[key] || []).length;
                return (
                  <button key={key} data-testid={`agenda-month-day-${key}`} onClick={() => setSelectedDay(key)}
                    className={`min-h-[72px] sm:min-h-[96px] border-b border-r border-slate-100 p-1 text-left transition-colors hover:bg-sky-50/40 ${!isSameMonth(d, cursor) ? "bg-slate-50 text-slate-300" : ""} ${dayBlock ? "bg-rose-50" : isWeekendKey(d) ? "bg-sky-50/50" : ""} ${selectedDay === key ? "ring-2 ring-inset ring-sky-500" : ""}`}>
                    <p className={`text-[11px] font-semibold mb-1 ${isToday(d) ? "text-white bg-sky-500 rounded-full h-5 w-5 flex items-center justify-center" : ""}`}>{format(d, "d")}</p>
                    {dayBlock && <p className={`text-[9px] font-bold mb-0.5 ${dayBlock.provisional ? "text-amber-700" : "text-rose-600"}`}>{dayBlock.provisional ? "À CONFIRMER" : "INDISPO"}</p>}
                    {dayEvents.length > 0 && (
                      <p className="text-[10px] font-semibold text-sky-700 pl-0.5">● {dayEvents.length} prestation{dayEvents.length > 1 ? "s" : ""}</p>
                    )}
                    {extraMoves > 0 && <p className="text-[9px] text-slate-500 pl-0.5">↕ {extraMoves} livraison{extraMoves > 1 ? "s" : ""}/reprise{extraMoves > 1 ? "s" : ""}</p>}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedDay && (
            <section data-testid="agenda-day-detail" className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-heading font-bold capitalize">{format(new Date(selectedDay + "T00:00:00"), "EEEE d MMMM yyyy", { locale: fr })}</h3>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid="agenda-day-open-week" onClick={() => openWeek(selectedDay)}>Voir la semaine</Button>
                  <Button size="sm" variant="ghost" data-testid="agenda-day-close" onClick={() => setSelectedDay(null)}>Fermer</Button>
                </div>
              </div>
              {blockFor(selectedDay) && <p className={`text-xs font-semibold ${blockFor(selectedDay).provisional ? "text-amber-700" : "text-rose-700"}`}>{blockFor(selectedDay).provisional ? "🟠 Période à confirmer" : "Indisponible"} — {blockFor(selectedDay).reason}{blockFor(selectedDay).relais_possible ? " (relais Stéphane possible)" : ""}</p>}
              <div className="space-y-1.5">
                {(byDay[selectedDay] || []).map((r) => <CompactLine key={r.id} r={r} boothNames={boothNames} />)}
              </div>
              {(byDelivery[selectedDay] || []).map((r) => <p key={`d${r.id}`} className="text-xs text-slate-600 flex items-center gap-1.5"><Truck className="h-3 w-3 text-sky-600" /> {(r.kind || "photobooth") === "photobooth" ? "Livraison →" : "Retrait →"} {r.event?.city} ({r.client?.name})</p>)}
              {(byPickup[selectedDay] || []).map((r) => <p key={`p${r.id}`} className="text-xs text-slate-600 flex items-center gap-1.5"><PackageCheck className="h-3 w-3 text-slate-500" /> {(r.kind || "photobooth") === "photobooth" ? "Reprise ←" : "Retour ←"} {r.event?.city} ({r.client?.name})</p>)}
              {(byDay[selectedDay] || []).length === 0 && (byDelivery[selectedDay] || []).length === 0 && (byPickup[selectedDay] || []).length === 0 && !blockFor(selectedDay) && (
                <p className="text-sm text-slate-400">Aucune prestation ce jour.</p>
              )}
            </section>
          )}
        </>
      )}

      {view === "year" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="agenda-year-view">
          {yearStats.map((ys) => {
            const mDate = new Date(cursor.getFullYear(), ys.month, 1);
            const intensity = ys.count >= 10 ? "bg-slate-900 text-white border-slate-900" : ys.count >= 4 ? "bg-sky-100 border-sky-300" : "bg-white";
            const isCurrentMonth = ys.month === new Date().getMonth() && cursor.getFullYear() === new Date().getFullYear();
            return (
              <button key={ys.month} data-testid={`agenda-year-month-${ys.month}`} onClick={() => openMonth(ys.month)}
                className={`rounded-2xl border border-slate-200 p-4 text-left hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] ${intensity} ${isCurrentMonth ? "ring-2 ring-sky-500" : ""}`}>
                <p className="font-heading font-extrabold uppercase tracking-tight">{format(mDate, "MMMM", { locale: fr })}</p>
                <p className={`text-sm mt-1 ${ys.count >= 10 ? "text-slate-300" : "text-slate-600"}`}>{ys.count} réservation{ys.count > 1 ? "s" : ""}</p>
                <p className={`text-xs ${ys.count >= 10 ? "text-slate-400" : "text-slate-400"}`}>{ys.weekends} week-end{ys.weekends > 1 ? "s" : ""} occupé{ys.weekends > 1 ? "s" : ""}</p>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap gap-3 pt-1" data-testid="agenda-legend">
        {Object.entries(STATUS_STYLES).filter(([s]) => !["Nouvelle demande internet", "Demande", "Option"].includes(s)).map(([s, st]) => (
          <span key={s} className="inline-flex items-center gap-1.5 text-xs text-slate-500">
            <span className={`h-2 w-2 rounded-full ${st.dot}`} /> {s}
          </span>
        ))}
      </div>

      {blocks.length > 0 && (
        <section className="rounded-2xl border border-rose-200 bg-white p-4" data-testid="unavailability-list">
          <h2 className="font-heading font-semibold mb-2 text-rose-700">Périodes bloquées</h2>
          <ul className="divide-y divide-slate-100">
            {blocks.map((b) => (
              <li key={b.id} className="py-2 flex items-center justify-between gap-2 text-sm">
                <span>
                  {b.provisional
                    ? <span className="inline-block rounded-full bg-amber-100 text-amber-800 text-[10px] font-extrabold uppercase px-2 py-0.5 mr-1.5" data-testid={`unavailability-provisional-${b.id}`}>À confirmer</span>
                    : <span className="inline-block rounded-full bg-rose-100 text-rose-700 text-[10px] font-extrabold uppercase px-2 py-0.5 mr-1.5">Indisponible</span>}
                  <span className="font-medium">{b.start_date} → {b.end_date}</span>
                  <span className="text-slate-500"> · {b.reason}{b.relais_possible ? " (relais Stéphane possible)" : ""}{(b.scope || []).length ? ` · ${(b.scope || []).map((s) => ({ photobooth: "Photobooth", agb: "AGB", cabine: "Cabine" })[s] || s).join(" + ")}` : ""}{b.all_day ? "" : ` · ${b.start_time}–${b.end_time}`}{b.comment ? ` · ${b.comment}` : ""}</span>
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  {b.provisional && (
                    <Button variant="outline" size="sm" data-testid={`unavailability-confirm-${b.id}`} onClick={() => confirmBlock(b.id)} className="border-amber-400 text-amber-800 hover:bg-amber-50">
                      Confirmer l'indisponibilité
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" data-testid={`unavailability-delete-${b.id}`} onClick={() => removeBlock(b.id)} className="text-rose-600 hover:bg-rose-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
